import { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from '@emotion/styled'
import { keyframes } from '@emotion/react'
import { PageLoadContext } from './PageLoadContext'
import createPageLoadTracker from './createPageLoadTracker'

const shimmer = keyframes`
  from { transform: translateX(-100%); }
  to { transform: translateX(100%); }
`
const appear = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`
const Frame = styled.div`
  position: relative;
  min-height: 320px;
  height: 100%;
  .admin-page-content[data-ready='false'] {
    position: absolute;
    inset: 0;
    visibility: hidden;
    pointer-events: none;
  }
  .admin-page-content[data-ready='true'] {
    height: 100%;
    animation: ${appear} 300ms ease-out;
  }
  .admin-page-skeleton {
    padding: 28px;
  }
  .admin-page-skeleton .bone {
    position: relative;
    overflow: hidden;
    background: #eeebf3;
    border-radius: 6px;
    height: 18px;
  }
  .admin-page-skeleton .bone::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, transparent, #ffffffb3, transparent);
    animation: ${shimmer} 1.25s linear infinite;
  }
  .admin-page-skeleton .heading {
    width: 180px;
    height: 26px;
  }
  .admin-page-skeleton .breadcrumb {
    width: 120px;
    margin: 16px 0 36px;
  }
  .admin-page-skeleton .filters {
    display: flex;
    gap: 24px;
    margin-bottom: 32px;
  }
  .admin-page-skeleton .filters .bone {
    width: 35%;
    height: 40px;
  }
  .admin-page-skeleton .row {
    display: grid;
    grid-template-columns: 28px 1fr 1fr 32px;
    gap: 24px;
    padding: 20px 12px;
  }
  .admin-page-skeleton .form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 24px;
    padding: 20px 0;
  }
  .admin-page-skeleton .form-row .bone {
    height: 40px;
  }
  @media (max-width: 599px) {
    .admin-page-skeleton {
      padding: 20px 16px;
    }
    .admin-page-skeleton .row {
      gap: 12px;
    }
    .admin-page-skeleton .form-row {
      grid-template-columns: 1fr;
    }
  }
  @media (prefers-reduced-motion: reduce) {
    .admin-page-content[data-ready='true'],
    .admin-page-skeleton .bone::after {
      animation: none;
    }
  }
`

export function AdminPageMounted({ children }) {
  const tracker = useContext(PageLoadContext)
  useEffect(() => {
    tracker?.mounted()
  }, [tracker])
  return children
}

export default function AdminPageLoad({ children, form = false }) {
  const [tracker] = useState(createPageLoadTracker)
  const [phase, setPhase] = useState('waiting')
  const { t } = useTranslation()
  const label = t('common.loading')
  useEffect(() => {
    const unsubscribe = tracker.subscribe(setPhase)
    tracker.start()
    return () => {
      unsubscribe()
      tracker.dispose()
    }
  }, [tracker])
  const ready = phase === 'ready'
  return (
    <PageLoadContext.Provider value={tracker}>
      <Frame aria-busy={!ready}>
        {phase === 'skeleton' && (
          <div className="admin-page-skeleton" role="status" aria-label={label}>
            <div aria-hidden="true">
              <div className="bone heading" />
              <div className="bone breadcrumb" />
              {!form && (
                <div className="filters">
                  <div className="bone" />
                  <div className="bone" />
                </div>
              )}
              {[0, 1, 2].map((row) => (
                <div className={form ? 'form-row' : 'row'} key={row}>
                  {(form ? [0, 1] : [0, 1, 2, 3]).map((cell) => (
                    <div className="bone" key={cell} />
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}
        <div
          className="admin-page-content"
          data-ready={ready}
          aria-hidden={!ready || undefined}
        >
          {children}
        </div>
      </Frame>
    </PageLoadContext.Provider>
  )
}
